import {KeyData, OfficeVisitSheet} from "./Model";
import {create} from "zustand";
import {useVisitReportStore} from "./VisitReportStore";
import {useKeyDataStore} from "./KeyDataStore";
import {RemoteValue} from "../util/RemoteValue";
import {derivedRemoteStore} from "./DerivedStore";
import {associateBy, getOrSet} from "../util/MapUtil";
import {combineCmp, numberCmp, stringCmp} from "../util/SortUtil";
import {employeeVisitType, EmployeeVisitType} from "./EmployeeVisitStore";

export interface ContractorRef {
    fullName: string;
    company?: string;
}

export type ContractorVisitSheet = OfficeVisitSheet<ContractorRef, EmployeeVisitType>;

export const useContractorVisitStore = create<{ remoteValue: RemoteValue<ContractorVisitSheet> }>()(
    derivedRemoteStore(
        {
            keyData: useKeyDataStore,
            visitReports: useVisitReportStore,
        },
        ({keyData, visitReports}) =>
            calculateContractorVisitSheet(keyData, visitReports.map(report => report.sheet)),
    )
);

function contractorId(contractor: ContractorRef): string {
    return `${contractor.fullName.trim().toLocaleLowerCase()}\u0000${contractor.company?.trim().toLocaleLowerCase() ?? ''}`;
}

export function calculateContractorVisitSheet(
    keyData: KeyData[],
    keyVisitSheets: OfficeVisitSheet<KeyData['keyNumber']>[],
): ContractorVisitSheet {
    const keyByNumber = associateBy(keyData, key => key.keyNumber);
    const dateSet = new Set<string>();
    const contractorVisits = new Map<string, { contractor: ContractorRef, dates: Set<string> }>();

    for (const sheet of keyVisitSheets) {
        sheet.dates.forEach(date => dateSet.add(date));
        sheet.rows.forEach(row => {
            const key = keyByNumber.get(row.value);
            if (!key) return;

            row.visits.forEach((visited, dateIndex) => {
                const date = sheet.dates[dateIndex];
                if (!visited || !date) return;

                key.history
                    .filter(record =>
                        record.location.type === 'with-guest' &&
                        (!record.from || record.from <= date) &&
                        (!record.to || date <= record.to)
                    )
                    .forEach(record => {
                        if (record.location.type !== 'with-guest') return;
                        const contractor = {
                            fullName: record.location.fullName.trim(),
                            company: record.location.company?.trim() || undefined,
                        };
                        getOrSet(contractorVisits, contractorId(contractor), () => ({
                            contractor,
                            dates: new Set<string>(),
                        })).dates.add(date);
                    });
            });
        });
    }

    const dates = [...dateSet].sort(stringCmp((date: string) => date));
    const contractors = [...contractorVisits.values()].sort(combineCmp(
        numberCmp(value => value.dates.size, true),
        stringCmp(value => value.contractor.fullName),
        stringCmp(value => value.contractor.company ?? ''),
    ));

    const rows = contractors.map(({contractor, dates: visitDates}) => ({
        value: contractor,
        visits: dates.map(date => employeeVisitType(date, visitDates.has(date))),
    }));
    return {dates, rows, length: rows.length};
}
